export type CollectionLink = {
  title: string
  href: string
  description?: string
}

export type CollectionPageData = {
  slug: string
  title: string
  description: string
  excerpt: string
  eyebrow: string
  intro: string[]
  bullets: string[]
  recipes: CollectionLink[]
  relatedLinks?: CollectionLink[]
}

export type SeasonalHub = {
  slug: "summer" | "autumn" | "winter" | "spring"
  title: string
  description: string
  shortDescription: string
  icon: string
  seasonLabels: string[]
  featuredRecipeSlugs: string[]
  featuredGuideSlugs: string[]
  featuredCalculatorLinks: CollectionLink[]
  relatedLinks?: CollectionLink[]
  scenarios: string[]
}

export const collections: CollectionPageData[] = [
  {
    slug: "dlya-pochatkivciv",
    title: "Прості рецепти заготовок для початківців",
    description:
      "Добірка простих і зрозумілих рецептів для тих, хто лише починає знайомство з домашньою консервацією, варенням і сезонними заготовками.",
    excerpt:
      "Надійні рецепти для першого сезону домашніх заготовок: без зайвих складнощів, з чіткими інгредієнтами та зрозумілими кроками.",
    eyebrow: "Для початківців",
    intro: [
      "Якщо ви тільки починаєте робити домашні заготовки, найкраще стартувати з простих рецептів із зрозумілими пропорціями та коротким списком інгредієнтів.",
      "У цій добірці зібрані варіанти, з яких зручно почати: вони не перевантажені складними етапами й допомагають відчути впевненість уже з першої партії.",
    ],
    bullets: [
      "просте покрокове приготування",
      "зрозумілі базові інгредієнти",
      "підходить для першого досвіду",
      "зручні рецепти для невеликої партії",
    ],
    recipes: [
      {
        title: "Мариновані огірки на зиму — класичний рецепт",
        href: "/recipes/marinovani-ogirky-na-zymu-klasychni",
      },
      {
        title: "Солодкі помідори на зиму в маринаді",
        href: "/recipes/solodki-pomidory-na-zymu",
      },
      {
        title: "Яблучний компот на зиму",
        href: "/recipes/yabluchnyi-kompot-na-zymu",
      },
      {
        title: "Полуничне варення-п’ятихвилинка",
        href: "/recipes/polunyczne-varennya-pyatykhvylynka",
      },
    ],
    relatedLinks: [
      {
        title: "Як стерилізувати банки для консервації",
        href: "/guides/sterilizaciya-banok",
      },
      {
        title: "Калькулятори пропорцій",
        href: "/calculators",
      },
      {
        title: "Літній сезон заготовок",
        href: "/seasonal/summer",
      },
    ],
  },
  {
    slug: "bez-octu",
    title: "Рецепти без оцту",
    description:
      "Добірка рецептів без оцту для тих, хто шукає більш натуральні смаки, ферментацію та м’які домашні заготовки.",
    excerpt:
      "Підібрали рецепти без оцту: ферментація, квашення та інші варіанти домашніх заготовок із м’якішим смаком.",
    eyebrow: "М’які смаки",
    intro: [
      "Не всі домашні заготовки потребують оцту. Часто користувачі шукають саме рецепти з м’якішим смаком або натуральні способи збереження продуктів.",
      "У цій добірці зібрані рецепти, де акцент зроблено на ферментації, природному смаку овочів і простих домашніх підходах.",
    ],
    bullets: [
      "добірка для тих, хто не любить різкий смак оцту",
      "акцент на ферментації та природному смаку",
      "зручно для тих, хто хоче спробувати квашення",
    ],
    recipes: [
      {
        title: "Квашена капуста — класичний домашній варіант",
        href: "/recipes/kvashena-kapusta-klasychna",
      },
    ],
    relatedLinks: [
      {
        title: "Ферментація для початківців",
        href: "/guides/fermentaciya-dlya-pochatkivciv",
      },
      {
        title: "Каталог рецептів",
        href: "/recipes",
      },
      {
        title: "Осінній сезон заготовок",
        href: "/seasonal/autumn",
      },
    ],
  },
  {
    slug: "shcho-prygotuvaty-z-kabachkiv",
    title: "Що приготувати з кабачків",
    description:
      "Практична добірка рецептів із кабачків для сезону: салати, заготовки та прості домашні ідеї, коли кабачків багато.",
    excerpt:
      "Ідеї для сезону кабачків: домашні заготовки, салати та прості способи використати врожай смачно й без зайвого клопоту.",
    eyebrow: "Сезон кабачків",
    intro: [
      "Коли кабачків стає багато, хочеться знайти кілька перевірених і справді практичних рецептів, а не губитися серед десятків випадкових порад.",
      "Ця добірка допомагає швидко зорієнтуватися й обрати домашні рецепти, які зручно готувати на щодень або закручувати на зиму.",
    ],
    bullets: [
      "ідеї для великої сезонної партії кабачків",
      "домашні заготовки та овочеві салати",
      "зручно для літа та початку осені",
    ],
    recipes: [
      {
        title: "Кабачковий салат на зиму",
        href: "/recipes/kabachkovyi-salat-na-zymu",
      },
    ],
    relatedLinks: [
      {
        title: "Усі рецепти",
        href: "/recipes",
      },
      {
        title: "Добірки рецептів",
        href: "/collections",
      },
      {
        title: "Осінній сезон заготовок",
        href: "/seasonal/autumn",
      },
    ],
  },
]

export const seasonalHubs: SeasonalHub[] = [
  {
    slug: "spring",
    title: "Весняний сезон домашньої кухні та підготовки до заготовок",
    description:
      "Весна — це період спокійного переходу між зимовими запасами і новим циклом сезонної кухні. У цей час зручно переглядати базові рецепти, оцінювати залишки заготовок, освіжати знання про безпеку і готуватися до активнішого літнього сезону.",
    shortDescription:
      "Використання запасів, спокійне планування, підготовка до нового сезону і перші легші домашні сценарії.",
    icon: "🌱",
    seasonLabels: ["spring"],
    featuredRecipeSlugs: [
      "kvashena-kapusta-klasychna",
      "yabluchnyi-kompot-na-zymu",
      "polunyczne-varennya-pyatykhvylynka",
    ],
    featuredGuideSlugs: [
      "yak-zberigaty-zagotovky",
      "sterilizaciya-banok",
    ],
    featuredCalculatorLinks: [
      {
        title: "Калькулятор солі та цукру",
        href: "/calculators/salt-sugar",
        description:
          "Зручно оновити пропорції, перевірити базові розрахунки і підготуватися до нового сезону.",
      },
      {
        title: "Калькулятори пропорцій",
        href: "/calculators",
        description:
          "Єдиний вхід у всі сервісні інструменти, коли хочеться спокійно навести лад у рецептах і розрахунках.",
      },
    ],
    relatedLinks: [
      {
        title: "Добірки для початківців",
        href: "/collections/dlya-pochatkivciv",
      },
      {
        title: "Гайди та поради",
        href: "/guides",
      },
    ],
    scenarios: [
      "Як спокійно переглянути зимові запаси і зрозуміти, що варто повторити в новому сезоні",
      "Що корисно перечитати перед початком літньої заготівлі",
      "Як підготувати кухню, інструменти і власний план без поспіху",
    ],
  },
  {
    slug: "summer",
    title: "Літній сезон заготовок",
    description:
      "Літо — це час огірків, ягід, перших кабачків, компотів, варення і старту активної домашньої консервації. У цьому сезонному хабі зібрані рецепти, гайди та калькулятори, які найчастіше потрібні саме влітку.",
    shortDescription:
      "Огірки, ягоди, варення, компоти, перші кабачки та активний старт сезону.",
    icon: "☀️",
    seasonLabels: ["summer"],
    featuredRecipeSlugs: [
      "marinovani-ogirky-na-zymu-klasychni",
      "polunyczne-varennya-pyatykhvylynka",
      "yabluchnyi-kompot-na-zymu",
    ],
    featuredGuideSlugs: [
      "sterilizaciya-banok",
      "yak-zberigaty-zagotovky",
    ],
    featuredCalculatorLinks: [
      {
        title: "Калькулятор маринаду",
        href: "/calculators/marinade",
        description:
          "Швидкий підрахунок маринаду під потрібну кількість банок.",
      },
      {
        title: "Сіль і цукор",
        href: "/calculators/salt-sugar",
        description:
          "Зручно масштабувати літні рецепти під велику сезонну партію.",
      },
    ],
    relatedLinks: [
      {
        title: "Що приготувати з кабачків",
        href: "/collections/shcho-prygotuvaty-z-kabachkiv",
      },
      {
        title: "Каталог рецептів",
        href: "/recipes",
      },
    ],
    scenarios: [
      "Що робити з великою кількістю огірків",
      "Як організувати сезон ягід, варення і компотів без зайвого поспіху",
      "Які літні заготовки варто зробити в першу чергу",
    ],
  },
  {
    slug: "autumn",
    title: "Осінній сезон заготовок",
    description:
      "Осінь — це час помідорів, капусти, ферментації, перцю, баклажанів і щільніших овочевих заготовок. У цьому сезонному хабі зібрані рецепти, сценарії та пояснення для найактивнішого етапу заготівельного сезону.",
    shortDescription:
      "Помідори, капуста, ферментація, овочеві салати та великі партії заготовок.",
    icon: "🍂",
    seasonLabels: ["autumn"],
    featuredRecipeSlugs: [
      "solodki-pomidory-na-zymu",
      "kvashena-kapusta-klasychna",
      "kabachkovyi-salat-na-zymu",
    ],
    featuredGuideSlugs: [
      "chomu-mutniye-rozsil",
      "fermentaciya-dlya-pochatkivciv",
    ],
    featuredCalculatorLinks: [
      {
        title: "Калькулятор маринаду",
        href: "/calculators/marinade",
        description: "Корисний для великих осінніх партій консервації.",
      },
      {
        title: "Конвертер оцту",
        href: "/calculators/vinegar-converter",
        description:
          "Допомагає не помилитися з концентрацією оцту у рецептах.",
      },
    ],
    relatedLinks: [
      {
        title: "Рецепти без оцту",
        href: "/collections/bez-octu",
      },
      {
        title: "Гайди з безпеки",
        href: "/guides",
      },
    ],
    scenarios: [
      "Як організувати великі партії заготовок восени",
      "Що приготувати з капусти, помідорів і кабачків у сезон",
      "Коли краще обрати ферментацію, а коли класичне маринування",
    ],
  },
  {
    slug: "winter",
    title: "Зимовий сезон домашніх запасів і спокійної кухні",
    description:
      "Зима — це час користування вже готовими запасами, квашених овочів, компотів, варення та уважного ставлення до зберігання. У цьому сезонному хабі зібрані рецепти, поради й інструменти для спокійної домашньої кухні без поспіху.",
    shortDescription:
      "Використання запасів, квашення, зберігання, компоти, варення і домашня кухня на основі вже готових заготовок.",
    icon: "❄️",
    seasonLabels: ["winter"],
    featuredRecipeSlugs: [
      "kvashena-kapusta-klasychna",
      "yabluchnyi-kompot-na-zymu",
      "polunyczne-varennya-pyatykhvylynka",
    ],
    featuredGuideSlugs: [
      "yak-zberigaty-zagotovky",
      "chomu-mutniye-rozsil",
    ],
    featuredCalculatorLinks: [
      {
        title: "Конвертер оцту",
        href: "/calculators/vinegar-converter",
        description:
          "Корисно, коли потрібно перевірити або уточнити пропорції під час зимового використання рецептів.",
      },
      {
        title: "Калькулятори пропорцій",
        href: "/calculators",
        description:
          "Швидкий доступ до всіх інструментів, якщо хочеться спокійно перевірити розрахунки і рецепти.",
      },
    ],
    relatedLinks: [
      {
        title: "Як зберігати домашні заготовки",
        href: "/guides/yak-zberigaty-zagotovky",
      },
      {
        title: "Каталог рецептів",
        href: "/recipes",
      },
    ],
    scenarios: [
      "Як користуватися зимовими запасами і частіше повертатися до вдалих рецептів",
      "На що звертати увагу під час зберігання банок у холодний період",
      "Як спокійно оцінити, які заготовки справді варто повторити в наступному сезоні",
    ],
  },
]

export function getAllCollections() {
  return collections
}

export function getCollectionBySlug(slug: string) {
  return collections.find((collection) => collection.slug === slug)
}

export function getCollectionSlugs() {
  return collections.map((collection) => collection.slug)
}

export function getAllSeasonalHubs() {
  return seasonalHubs
}

export function getSeasonalHubBySlug(slug: SeasonalHub["slug"]) {
  return seasonalHubs.find((hub) => hub.slug === slug)
}